import { supabase } from '../lib/supabase';

export interface PlatformPaymentMethod {
  id: string;
  payment_method_type: 'card' | 'bank' | 'paypal' | 'apple' | 'google';
  payment_method_name: string;
  payment_method_last4?: string;
  payment_method_expiry?: string;
  payment_method_email?: string;
  payment_method_is_default: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface CreatePlatformPaymentMethodData {
  payment_method_type: 'card' | 'bank' | 'paypal' | 'apple' | 'google';
  payment_method_name: string;
  payment_method_last4?: string;
  payment_method_expiry?: string;
  payment_method_email?: string;
  payment_method_is_default?: boolean;
}

export interface PlatformFeeSettings {
  fee_type: 'fixed' | 'percentage';
  fee_amount: number; // Fixed fee amount in dollars
  fee_percentage: number | null; // Percentage fee (e.g., 2.5 for 2.5%)
}

export class PlatformSettingsService {
  private static cachedPayPalEmail: { value: string; fetchedAt: number } | null = null;

  /**
   * Get all platform payment methods
   */
  static async getPlatformPaymentMethods(): Promise<PlatformPaymentMethod[]> {
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .not('payment_method_type', 'is', null)
        .order('payment_method_is_default', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching platform payment methods:', error);
        throw error;
      }
      return data || [];
    } catch (error) {
      console.error('Error in getPlatformPaymentMethods:', error);
      throw error;
    }
  }

  /**
   * Add a new platform payment method
   * Only one platform payment method is allowed
   */
  static async addPlatformPaymentMethod(
    paymentMethod: CreatePlatformPaymentMethodData
  ): Promise<PlatformPaymentMethod> {
    try {
      // Check if there's already a payment method
      const existingMethods = await this.getPlatformPaymentMethods();
      if (existingMethods.length >= 1) {
        throw new Error('Only one platform payment method is allowed. Please remove the existing one before adding a new one.');
      }

      // Get current user for created_by
      const { data: { user } } = await supabase.auth.getUser();
      
      const insertData = {
        ...paymentMethod,
        created_by: user?.id,
        updated_by: user?.id,
      };

      const { data, error } = await supabase
        .from('platform_settings')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Error adding platform payment method:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in addPlatformPaymentMethod:', error);
      throw error;
    }
  }

  /**
   * Update an existing platform payment method
   */
  static async updatePlatformPaymentMethod(
    id: string,
    updates: Partial<CreatePlatformPaymentMethodData>
  ): Promise<PlatformPaymentMethod> {
    try {
      // Get current user for updated_by
      const { data: { user } } = await supabase.auth.getUser();
      
      const updateData = {
        ...updates,
        updated_by: user?.id,
      };

      const { data, error } = await supabase
        .from('platform_settings')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating platform payment method:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updatePlatformPaymentMethod:', error);
      throw error;
    }
  }

  /**
   * Delete a platform payment method
   */
  static async deletePlatformPaymentMethod(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('platform_settings')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting platform payment method:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deletePlatformPaymentMethod:', error);
      throw error;
    }
  }

  /**
   * Set a platform payment method as default (automatically unsets others)
   */
  static async setDefaultPlatformPaymentMethod(id: string): Promise<void> {
    try {
      // First, unset all other default payment methods
      await supabase
        .from('platform_settings')
        .update({ payment_method_is_default: false })
        .neq('id', id)
        .not('payment_method_type', 'is', null);

      // Then set this one as default
      const { error } = await supabase
        .from('platform_settings')
        .update({ payment_method_is_default: true })
        .eq('id', id);

      if (error) {
        console.error('Error setting default platform payment method:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in setDefaultPlatformPaymentMethod:', error);
      throw error;
    }
  }

  /**
   * Get the default platform payment method
   */
  static async getDefaultPlatformPaymentMethod(): Promise<PlatformPaymentMethod | null> {
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('payment_method_is_default', true)
        .not('payment_method_type', 'is', null)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error fetching default platform payment method:', error);
        throw error;
      }

      return data || null;
    } catch (error) {
      console.error('Error in getDefaultPlatformPaymentMethod:', error);
      throw error;
    }
  }

  static async getPlatformPayPalEmail(opts?: { forceRefresh?: boolean }): Promise<string> {
    const forceRefresh = !!opts?.forceRefresh;
    const now = Date.now();

    if (!forceRefresh && this.cachedPayPalEmail && now - this.cachedPayPalEmail.fetchedAt < 60_000) {
      return this.cachedPayPalEmail.value;
    }

    try {
      const { data: defRow, error: defErr } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('payment_method_type', 'paypal')
        .eq('payment_method_is_default', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!defErr && defRow?.payment_method_email) {
        const email = String(defRow.payment_method_email).trim();
        if (email) {
          this.cachedPayPalEmail = { value: email, fetchedAt: now };
          return email;
        }
      }

      const { data: anyRow, error: anyErr } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('payment_method_type', 'paypal')
        .order('payment_method_is_default', { ascending: false })
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!anyErr && anyRow?.payment_method_email) {
        const email = String(anyRow.payment_method_email).trim();
        if (email) {
          this.cachedPayPalEmail = { value: email, fetchedAt: now };
          return email;
        }
      }
    } catch (error) {
      console.error('Error in getPlatformPayPalEmail:', error);
    }

    throw new Error('No platform PayPal email configured. Please set it in Admin → Website Settings.');
  }

  /**
   * Get the current platform fee settings
   * Returns fee type, amount, and percentage
   */
  static async getPlatformFeeSettings(): Promise<PlatformFeeSettings> {
    try {
      // Look for a row with payment_method_type null (general settings row)
      const { data, error } = await supabase
        .from('platform_settings')
        .select('fee_type, platform_fee_amount, fee_percentage')
        .is('payment_method_type', null)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching platform fee settings:', error);
        // Return defaults
      }

      // Return the settings or defaults
      if (data) {
        return {
          fee_type: (data.fee_type as 'fixed' | 'percentage') || 'fixed',
          fee_amount: data.platform_fee_amount ? parseFloat(data.platform_fee_amount.toString()) : 0.50,
          fee_percentage: data.fee_percentage ? parseFloat(data.fee_percentage.toString()) : null,
        };
      }

      // Default values
      return {
        fee_type: 'fixed',
        fee_amount: 0.50,
        fee_percentage: null,
      };
    } catch (error) {
      console.error('Error in getPlatformFeeSettings:', error);
      // Return default values if there's an error
      return {
        fee_type: 'fixed',
        fee_amount: 0.50,
        fee_percentage: null,
      };
    }
  }

  /**
   * Update the platform fee settings
   * @param settings - The platform fee settings (type, amount, percentage)
   */
  static async updatePlatformFeeSettings(settings: PlatformFeeSettings): Promise<void> {
    try {
      // Get current user for updated_by
      const { data: { user } } = await supabase.auth.getUser();

      // Prepare update data
      const updateData: any = {
        fee_type: settings.fee_type,
        updated_by: user?.id,
      };

      if (settings.fee_type === 'fixed') {
        updateData.platform_fee_amount = settings.fee_amount;
        updateData.fee_percentage = null;
      } else {
        updateData.fee_percentage = settings.fee_percentage;
        updateData.platform_fee_amount = null;
      }

      // First, try to find an existing platform fee settings row
      const { data: existingData } = await supabase
        .from('platform_settings')
        .select('id')
        .is('payment_method_type', null)
        .limit(1)
        .maybeSingle();

      if (existingData) {
        // Update existing row
        const { error } = await supabase
          .from('platform_settings')
          .update(updateData)
          .eq('id', existingData.id);

        if (error) {
          console.error('Error updating platform fee settings:', error);
          throw error;
        }
      } else {
        // Create new row for platform fee settings
        const { error } = await supabase
          .from('platform_settings')
          .insert([{
            ...updateData,
            payment_method_type: null,
            created_by: user?.id,
          }]);

        if (error) {
          console.error('Error creating platform fee settings:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error in updatePlatformFeeSettings:', error);
      throw error;
    }
  }

  /**
   * Get the current platform fee amount (legacy method for backward compatibility)
   * Returns the platform fee amount in dollars (default: 0.50)
   */
  static async getPlatformFee(): Promise<number> {
    const settings = await this.getPlatformFeeSettings();
    return settings.fee_amount;
  }

  /**
   * Update the platform fee amount (legacy method for backward compatibility)
   * @param feeAmount - The new platform fee amount in dollars
   */
  static async updatePlatformFee(feeAmount: number): Promise<void> {
    const settings = await this.getPlatformFeeSettings();
    await this.updatePlatformFeeSettings({
      ...settings,
      fee_amount: feeAmount,
      fee_type: 'fixed',
    });
  }
}

